import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useOutage } from '../context/OutageContext';

export default function GlobalOutageOverlay() {
  const { active, until, reason } = useOutage();

  const [now, setNow] = useState(Date.now());
  const initialMsRef = useRef(null); // ⬅️ sin tipos TS
  const timerRef = useRef(null);     // ⬅️ sin tipos TS
  const reloadedRef = useRef(false); // ⬅️ sin tipos TS

  // Inicia/actualiza timer y captura ventana inicial para el progreso
  useEffect(() => {
    if (!active) return;

    if (until) initialMsRef.current = Math.max(0, until - Date.now());

    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = window.setInterval(() => setNow(Date.now()), 1000);

    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [active, until]);

  const remainingSec = useMemo(() => {
    if (!until) return null;
    return Math.max(0, Math.ceil((until - now) / 1000));
  }, [until, now]);

  // Auto recarga al llegar a 0 (con guard anti-duplicado)
  useEffect(() => {
    if (!active) return;
    if (remainingSec === 0 && until && !reloadedRef.current) {
      reloadedRef.current = true;
      setTimeout(() => window.location.reload(), 750);
    }
  }, [active, remainingSec, until]);

  if (!active) return null;

  const fmt = (sec) => {
    if (sec == null) return '…';
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const totalMs = initialMsRef.current;
  const leftMs = until ? Math.max(0, until - now) : null;
  const pct = totalMs
    ? Math.max(0, Math.min(100, Math.round(((leftMs ?? 0) / totalMs) * 100)))
    : 100;

  return (
    <div
      role="alertdialog"
      aria-live="assertive"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2147483647,
        // Fondo opaco (no transparente)
        backgroundColor: '#0f172a', // slate-900
        color: '#e5e7eb',           // gray-200
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 520,
          backgroundColor: '#111827', // gray-900
          borderRadius: 16,
          padding: 24,
          boxShadow: '0 20px 40px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <div
            aria-hidden
            style={{
              width: 46,
              height: 46,
              borderRadius: 12,
              backgroundColor: '#1f2937', // gray-800
              display: 'grid',
              placeItems: 'center',
              boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.06)',
            }}
          >
            {/* Icono de advertencia */}
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 8v5" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" />
              <circle cx="12" cy="17" r="1.25" fill="#f59e0b" />
              <path
                d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
                stroke="#f59e0b"
                strokeWidth="2"
                fill="none"
              />
            </svg>
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#f9fafb', margin: 0 }}>
            No podemos atender tu solicitud ahora mismo
          </h1>
        </div>

        <p style={{ margin: '8px 0 16px', color: '#d1d5db' }}>
          {reason === 'rate-limit'
            ? 'Demasiadas peticiones al servicio.'
            : 'El servicio está temporalmente no disponible.'}{' '}
          Reintentaremos automáticamente en{' '}
          <strong style={{ color: '#f9fafb' }}>{fmt(remainingSec)}</strong>.
        </p>

        {/* Barra de progreso */}
        <div style={{ margin: '12px 0 20px' }}>
          <div
            style={{
              height: 10,
              borderRadius: 999,
              backgroundColor: '#1f2937',
              overflow: 'hidden',
              boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.4)',
            }}
          >
            <div
              style={{
                width: `${pct}%`,
                height: '100%',
                backgroundImage: 'linear-gradient(90deg,#f59e0b,#fbbf24)',
                transition: 'width .3s ease',
              }}
            />
          </div>
          <div
            style={{
              marginTop: 8,
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 12,
              color: '#9ca3af',
            }}
          >
            <span>Esperando ventana de disponibilidad</span>
            <span>{pct}%</span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 16px',
              borderRadius: 10,
              border: '0',
              cursor: 'pointer',
              fontWeight: 600,
              backgroundImage: 'linear-gradient(90deg,#2563eb,#3b82f6)',
              color: 'white',
              boxShadow: '0 6px 16px rgba(59,130,246,0.35)',
            }}
          >
            Reintentar ahora
          </button>
          {remainingSec != null && (
            <div
              aria-live="polite"
              style={{
                alignSelf: 'center',
                fontVariantNumeric: 'tabular-nums',
                color: '#9ca3af',
              }}
            >
              Reinicio automático en {fmt(remainingSec)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
